/**
 * Layout classes.
 *
 * Structure only. No colours are chosen here beyond the semantic tokens — a
 * layout that decides its own visual treatment becomes a second design system.
 */
export const styles = {
  /** No padding — the shell's `<main>` owns page gutters. See `app-list-layout`. */
  page: 'flex flex-1 flex-col gap-6',
  header: 'flex flex-wrap items-start justify-between gap-4',
  headerText: 'flex flex-col gap-1',
  backLink: 'text-sm text-content-muted underline-offset-4 hover:underline',
  title: 'text-2xl font-semibold text-content',
  subtitle: 'text-sm text-content-muted',
  headerAside: 'flex items-center gap-3',
  actions: 'flex items-center gap-2',
  toolbar: 'rounded-surface border border-border bg-surface-raised p-4',
  body: 'flex flex-1 flex-col gap-6 lg:flex-row',
  main: 'flex min-w-0 flex-1 flex-col gap-6',
  aside: 'flex w-full shrink-0 flex-col gap-4 lg:w-80',

  sectionGrid: 'grid grid-cols-1 gap-6 xl:grid-cols-2',
  section: 'flex flex-col gap-3 rounded-surface border border-border bg-surface-raised p-4',
  sectionHeading: 'text-sm font-medium tracking-wide text-content-muted uppercase',

  /**
   * Tab strip. `overflow-x-auto` lets long tab lists scroll on narrow
   * viewports; `gap-2 sm:gap-6` keeps icons + labels close on mobile and
   * gives the active state room to breathe on desktop.
   */
  tabbed: 'flex flex-col gap-4',
  tabList: 'flex items-center gap-2 overflow-x-auto border-b border-border sm:gap-6',
  /**
   * The tab button: leading icon (optional), label, trailing count (optional).
   * The bottom border is 2px and the negative `-mb-px` lets it sit on top of
   * the tabList's own border without doubling up.
   */
  tab: 'inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap border-b-2 pb-3 pt-2 text-sm font-semibold transition-colors -mb-px',
  /**
   * The active tab is a positive colour state, not just a different border.
   * The text colour and font weight change alongside the border so the
   * selection is visible to anyone who cannot distinguish the accent hue.
   */
  tabActive: 'border-accent text-accent font-bold',
  tabInactive: 'border-transparent text-content-muted hover:text-content',
  /**
   * Decorative icon. Inherits the colour from the parent tab so the active
   * state propagates to the icon without a separate rule.
   */
  tabIcon: 'inline-flex h-4 w-4 shrink-0 items-center justify-center',
  /**
   * The count pill: rounded, slightly tinted, sitting to the right of the
   * label. The active state carries the accent colour so the badge visually
   * pairs with the active border.
   */
  tabCount:
    'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 font-mono text-[11px] font-semibold tabular-nums',
  tabCountActive: 'bg-accent/10 text-accent',
  tabCountInactive: 'bg-surface-sunken text-content-muted',

  panel: 'rounded-surface border border-border bg-surface-raised p-4',

  /**
   * Accent header card — commerce-style elevated card with breadcrumb + nav +
   * actions + tab strip in the footer.
   *
   * Token-aware: the layout's design system has no foreign palette colours,
   * so the dark gradient from the commerce reference is approximated with
   * `bg-surface-raised` + a `from-surface-raised via-surface to-surface-sunken`
   * gradient. The card stays visually distinct from the page surface in both
   * light and dark themes via the gradient, the strong border, and the
   * rounded-2xl shape.
   */
  accentCard:
    'relative overflow-hidden rounded-2xl border border-border-strong bg-gradient-to-br from-surface-raised via-surface to-surface-sunken shadow-xs',
  /**
   * The padded body of the accent card. The relative + z-10 keeps the body
   * above the optional ambient glow blobs layered behind it.
   *
   * Padding is intentionally tight — the title + status + subtitle only need
   * room to breathe, not a 32px gutter.
   */
  accentHeaderBody:
    'relative z-10 flex flex-col gap-2 px-5 py-4 sm:px-6 sm:py-5 md:flex-row md:items-center md:justify-between',
  accentHeaderText: 'flex flex-col gap-1',
  accentTitle:
    'flex items-center gap-2.5 flex-wrap text-xl font-extrabold tracking-tight text-content sm:text-2xl lg:text-3xl',
  accentSubtitle: 'text-xs text-content-muted sm:text-sm',
  /**
   * An inline badge next to the title. Uses the surface-sunken tone so the
   * existing `Badge` component reads correctly inside the card.
   */
  accentBadge: 'shadow-xs',

  /**
   * The cluster of primary + secondary buttons that sit inside the card body
   * (when the consumer does not use `primaryAction` above the card). The
   * accent variant now places actions outside the card by default, so this
   * is only used when the consumer explicitly chooses to keep them inside.
   */
  accentPrimaryActions: 'flex items-center gap-2',

  /**
   * Breadcrumb nav. `flex-wrap` lets long trails break onto a second line.
   * `text-md` (13px) — the breadcrumb is page navigation, not meta, so it
   * reads a step larger than the 11px `text-xs` it used to use.
   */
  breadcrumbs: 'flex flex-wrap items-center gap-1.5 text-md font-medium text-content-muted',
  /**
   * The row that places the breadcrumb + page-action strip above the accent
   * card. It pulls itself up by the page's own `gap-6` (`-mb-5`) and adds a
   * small `pb-1` so the visual gap between the breadcrumb text and the card
   * top is ~8px of whitespace, not the full 24px page gutter.
   */
  breadcrumbsRow: 'flex flex-wrap items-center justify-between gap-3 pb-1 -mb-5',
  /**
   * The action cluster that lives alongside the breadcrumb. Action buttons
   * here are styled like breadcrumb links (small, inline) so the whole row
   * reads as "page navigation + actions", not "a header card with actions".
   */
  pageActions: 'flex shrink-0 items-center gap-2',
  breadcrumbItem: 'inline-flex items-center gap-1.5',
  breadcrumbLink:
    'inline-flex cursor-pointer items-center gap-1 text-content-muted transition-colors hover:text-content',
  breadcrumbCurrent:
    'inline-flex max-w-[140px] items-center truncate font-semibold text-content sm:max-w-xs',
  breadcrumbSeparator: 'text-content-subtle',

  /** The paired prev/next record nav — a single grouped pill. */
  navGroup:
    'inline-flex items-center gap-1 rounded-lg border border-border-strong bg-surface-sunken p-0.5',
  navButton:
    'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-border-strong bg-surface-sunken text-content-muted transition-colors hover:bg-surface-raised hover:text-content disabled:pointer-events-none disabled:opacity-30',
  /** Removes the visual seam between the two buttons in the paired control. */
  navButtonRight: '',

  /**
   * Tab strip rendered inside the accent card footer. The footer sits on a
   * sunken surface so the tab strip reads as "underneath" the title row.
   */
  tabsFooter:
    'relative z-10 overflow-hidden border-t border-border bg-surface-sunken px-4 pt-1.5 sm:px-8',
  tabsFooterList:
    'flex w-full items-center gap-2 overflow-x-auto overflow-y-hidden border-b border-border sm:gap-6',
  tabsFooterTab:
    'group relative inline-flex shrink-0 cursor-pointer select-none items-center gap-2 whitespace-nowrap border-b-2 pb-3 pt-2 text-xs font-semibold transition-colors -mb-px sm:text-sm',
  tabsFooterTabActive: 'border-accent text-accent font-bold',
  tabsFooterTabInactive:
    'border-transparent font-medium text-content-muted hover:border-border-strong hover:text-content',
  tabsFooterIcon: 'h-4 w-4 shrink-0 transition-colors',
  tabsFooterCount:
    'rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold transition-colors',
  tabsFooterCountActive: 'bg-accent-soft text-accent',
  tabsFooterCountInactive: 'bg-surface text-content-muted group-hover:text-content',

  /**
   * Sticky bottom action bar shown on viewports narrower than `lg`
   * (Tailwind's 1024px). Use for the primary action that must stay reachable
   * on mobile.
   */
  mobileBar:
    'fixed right-0 bottom-0 left-0 z-40 flex items-center justify-between gap-3 border-t border-border/80 bg-surface/95 px-4 py-3 shadow-lg backdrop-blur-md lg:hidden',
  mobileBarInfo: 'flex min-w-0 flex-col',
  mobileBarLabel: 'truncate text-[11px] font-medium text-content-muted',
  mobileBarValue: 'truncate text-sm font-bold text-content',
  mobileBarActions: 'flex shrink-0 items-center gap-2',
} as const;